import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListSubheader,
    TextField,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';

import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { Contact } from '../../../sic-lib/types/Contact';
import { UserInfo } from '../../../sic-lib/types/UserConfig';
import { groupUsersByOwnership } from '../../../sic-lib/utils/functionalReducers';
import { useAppSelector } from '../../../store/hooks';
import FollowerDialogSelectItem from './FollowerDialogSelectItem';

interface Props {
    contact: Contact;
}

interface CompState {
    searchKeyword: string;
    compError: string;
    isOpen: boolean;
}

export default function FollowerDialogSelect({ contact }: Props) {
    const { classes } = useStyles();

    const [compState, setCompState] = React.useReducer<GenericReducer<CompState>>(genericReducer, {
        searchKeyword: '',
        compError: '',
        isOpen: false,
    });

    const users = useAppSelector(state => state.user.users);

    const sections = React.useMemo(() => {
        const searchedUsers = users.filter(user =>
            filterUsersBySearch(user, compState.searchKeyword),
        );
        return groupUsersByOwnership(searchedUsers || [], contact?.owners || []);
    }, [compState.searchKeyword, contact?.owners, users]);

    const handleClickOpen = () => {
        setCompState({ isOpen: true });
    };

    const handleClose = () => {
        setCompState({ isOpen: false });
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompState({ searchKeyword: event.target.value });
    };

    return (
        <React.Fragment>
            <AddCircleOutlineIcon
                className={classes.addOwnerBtn}
                onClick={handleClickOpen}
                color="action"
            />
            <Dialog onClose={handleClose} open={compState.isOpen} fullWidth>
                <DialogTitle>Contact Followers</DialogTitle>
                <DialogContent className={classes.dialogContainer}>
                    <TextField
                        label="Search"
                        error={!!compState.compError}
                        helperText={compState.compError}
                        onChange={handleChange}
                        className={classes.textField}
                        fullWidth
                    />
                    <Box className={classes.menuList}>
                        <List>
                            <ListSubheader>Following</ListSubheader>
                            {sections.followers.map((aUser, index) => (
                                <FollowerDialogSelectItem
                                    data={aUser}
                                    contactId={contact.contactId}
                                    key={index}
                                />
                            ))}
                        </List>
                        <List>
                            <ListSubheader>Unassigned</ListSubheader>
                            {sections.unassigned.map((aUser, index) => (
                                <FollowerDialogSelectItem
                                    data={aUser}
                                    contactId={contact.contactId}
                                    key={index}
                                />
                            ))}
                        </List>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    addOwnerBtn: {
        cursor: 'pointer',
    },
    dialogContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    menuList: {
        overflowY: 'scroll',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    textField: {},
}));

const filterUsersBySearch = (user: UserInfo, search: string) => {
    return (
        user.userName.toLowerCase().includes(search.toLowerCase()) ||
        `${user.firstName || ''} ${user.lastName || ''}`
            .trim()
            .toLowerCase()
            .includes(search.toLowerCase())
    );
};
